import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import axios from 'axios';
import { MapPin, Calendar, Home, Phone, MessageCircle, FileText, CheckCircle, X, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '@/context/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectDetail = () => {
  const { id } = useParams();
  const { settings } = useSettings();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showFloorPlanModal, setShowFloorPlanModal] = useState(false);
  const [selectedFloorPlan, setSelectedFloorPlan] = useState('');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const openLightbox = (url) => {
    setLightboxImage(url);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };
  const closeLightbox = () => {
    setLightboxImage(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };
  const zoomIn = () => setZoom((z) => Math.min(z + 0.5, 5));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.5, 1));
  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };
  const [floorPlanFormData, setFloorPlanFormData] = useState({
    name: '',
    phone: '',
    email: '',
  });
  const [submittingFloorPlan, setSubmittingFloorPlan] = useState(false);
  const [showFloorPlanViewer, setShowFloorPlanViewer] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const handleFloorPlanClick = (planUrl) => {
    setSelectedFloorPlan(planUrl);
    setShowFloorPlanModal(true);
  };

  const handleFloorPlanSubmit = async (e) => {
    e.preventDefault();

    // Validate: 10-digit phone, no '+' / spaces / country code in input field
    const phoneDigits = floorPlanFormData.phone.replace(/[^0-9]/g, '');
    if (phoneDigits.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    const fullPhone = `+91${phoneDigits}`;

    setSubmittingFloorPlan(true);

    try {
      await axios.post(`${API}/floor-plan-request`, {
        name: floorPlanFormData.name,
        phone: fullPhone,
        email: floorPlanFormData.email,
        project_name: project.name,
        floor_plan_url: selectedFloorPlan,
      });

      toast.success('Thank you! Opening floor plan...');

      // Send WhatsApp notification to admin (opens a new tab)
      const whatsappNumber = (settings.admin_whatsapp_number || settings.founders?.[0]?.whatsapp || '919511611575').replace(/[^0-9]/g, '');
      const message = `Floor Plan Request from Website!\n\nName: ${floorPlanFormData.name}\nPhone: ${fullPhone}\nEmail: ${floorPlanFormData.email}\nProject: ${project.name}\nLocation: ${project.location_area}, ${project.location_city}`;
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');

      // Close form, open inline viewer so the user always sees the floor plan
      // (even after returning from WhatsApp tab)
      setShowFloorPlanModal(false);
      setShowFloorPlanViewer(true);
      setFloorPlanFormData({ name: '', phone: '', email: '' });
    } catch (error) {
      console.error('Error submitting floor plan request:', error);
      toast.error('Failed to process request. Please try again.');
    } finally {
      setSubmittingFloorPlan(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <p className="text-gray-600 text-lg">Project not found</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Link to="/projects" className="text-blue-600 hover:underline" data-testid="back-to-projects">
              ← Back to Projects
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="bg-gray-100 rounded-xl overflow-hidden mb-4 h-96 flex items-center justify-center relative group" data-testid="main-project-image">
                {project.images && project.images.length > 0 ? (
                  <>
                    <img
                      src={project.images[selectedImage]}
                      alt={project.name}
                      className="max-w-full max-h-full object-contain cursor-zoom-in"
                      onClick={() => openLightbox(project.images[selectedImage])}
                      data-testid="main-project-image-clickable"
                    />
                    <button
                      onClick={() => openLightbox(project.images[selectedImage])}
                      className="absolute top-3 right-3 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Open full view"
                      data-testid="open-lightbox-btn"
                    >
                      <Maximize2 size={18} />
                    </button>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Home size={64} className="text-gray-400" />
                  </div>
                )}
              </div>
              {project.images && project.images.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {project.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`h-20 rounded-lg overflow-hidden border-2 ${
                        selectedImage === index ? 'border-blue-600' : 'border-gray-200'
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img src={image} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="project-name">
                {project.name}
              </h1>
              <a
                href={
                  project.location_map_url ||
                  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${project.name}, ${project.location_area}, ${project.location_city}`)}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-gray-600 hover:text-red-600 hover:underline transition-colors mb-4"
                data-testid="project-location-link"
                title="Open in Google Maps"
              >
                <MapPin size={20} />
                <span className="text-lg">{project.location_area}, {project.location_city}</span>
              </a>

              <div className="flex flex-wrap gap-3 mb-6">
                <span className={`status-badge status-${project.status}`}>
                  {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                </span>
                {project.apartment_types && project.apartment_types.map((type, index) => (
                  <span key={index} className="status-badge bg-gray-100 text-gray-800">
                    {type}
                  </span>
                ))}
              </div>

              <div className="space-y-4 mb-8">
                {project.expected_launch && project.status === 'upcoming' && (
                  <div className="flex items-center gap-3">
                    <Calendar size={20} className="text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Expected Launch</p>
                      <p className="font-medium">{project.expected_launch}</p>
                    </div>
                  </div>
                )}
                {project.construction_status && project.status === 'ongoing' && (
                  <div className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Construction Status</p>
                      <p className="font-medium">{project.construction_status}</p>
                    </div>
                  </div>
                )}
                {project.possession_timeline && project.status === 'ongoing' && (
                  <div className="flex items-center gap-3">
                    <Calendar size={20} className="text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Possession Timeline</p>
                      <p className="font-medium">{project.possession_timeline}</p>
                    </div>
                  </div>
                )}
                {project.year_completed && project.status === 'completed' && (
                  <div className="flex items-center gap-3">
                    <Calendar size={20} className="text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Year Completed</p>
                      <p className="font-medium">{project.year_completed}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <Link
                  to="/contact"
                  className="cta-button-primary text-center"
                  data-testid="book-site-visit-btn"
                >
                  Book Site Visit
                </Link>
                <a
                  href={`tel:${(settings.founders?.[0]?.phone) || '+919511611575'}`}
                  className="cta-button bg-white px-6 py-3 rounded-md font-semibold border-2 hover:bg-gray-50 flex items-center justify-center gap-2 uppercase text-sm"
                  style={{ color: '#C41E3A', borderColor: '#C41E3A' }}
                  data-testid="call-btn"
                >
                  <Phone size={18} />
                  Call Now
                </a>
                <a
                  href={`https://wa.me/${(settings.admin_whatsapp_number || settings.founders?.[0]?.whatsapp || '919511611575').replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-button bg-green-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-green-700 flex items-center justify-center gap-2 uppercase text-sm"
                  data-testid="whatsapp-btn"
                >
                  <MessageCircle size={18} />
                  WhatsApp
                </a>
              </div>

              <p className="text-gray-700 leading-relaxed" data-testid="project-description">
                {project.description}
              </p>
            </div>
          </div>

          {project.amenities && project.amenities.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Amenities</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {project.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center gap-2 text-gray-700" data-testid={`amenity-${index}`}>
                    <CheckCircle size={18} className="text-green-600" />
                    <span>{amenity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {project.floor_plans && project.floor_plans.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Floor Plans</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {project.floor_plans.map((plan, index) => (
                  <button
                    key={index}
                    onClick={() => handleFloorPlanClick(plan)}
                    className="border-2 rounded-xl p-8 hover:border-red-700 transition-colors flex flex-col items-center justify-center gap-4 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                    style={{ minHeight: '200px' }}
                    data-testid={`floor-plan-${index}`}
                  >
                    <FileText size={64} style={{ color: '#C41E3A' }} />
                    <div className="text-center">
                      <p className="font-semibold text-gray-900 mb-2">Floor Plan {index + 1}</p>
                      <p className="text-sm text-gray-600">Click to view floor plan</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {showFloorPlanModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl max-w-md w-full p-8 relative">
                <button
                  onClick={() => setShowFloorPlanModal(false)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                  data-testid="close-floor-plan-modal"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-4">View Floor Plan</h3>
                <p className="text-gray-600 mb-6">Please provide your details to view the floor plan</p>
                
                <form onSubmit={handleFloorPlanSubmit} className="space-y-4" data-testid="floor-plan-form">
                  <div>
                    <label htmlFor="fp-name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fp-name"
                      value={floorPlanFormData.name}
                      onChange={(e) => setFloorPlanFormData({ ...floorPlanFormData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      placeholder="Enter your name"
                      data-testid="fp-input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="fp-phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-600 text-sm font-medium">
                        +91
                      </span>
                      <input
                        type="tel"
                        id="fp-phone"
                        value={floorPlanFormData.phone}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                          setFloorPlanFormData({ ...floorPlanFormData, phone: digits });
                        }}
                        required
                        pattern="[0-9]{10}"
                        maxLength={10}
                        inputMode="numeric"
                        className="flex-1 w-full px-4 py-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                        placeholder="98765 43210"
                        data-testid="fp-input-phone"
                      />
                    </div>
                    {floorPlanFormData.phone && floorPlanFormData.phone.length !== 10 && (
                      <p className="text-xs text-red-600 mt-1">Enter a 10-digit mobile number</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="fp-email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="fp-email"
                      value={floorPlanFormData.email}
                      onChange={(e) => setFloorPlanFormData({ ...floorPlanFormData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      placeholder="your.email@example.com"
                      data-testid="fp-input-email"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submittingFloorPlan}
                    className="w-full text-white px-6 py-3 rounded-lg font-medium disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    style={{ background: submittingFloorPlan ? undefined : 'linear-gradient(135deg, #C41E3A 0%, #8B2635 100%)' }}
                    data-testid="fp-submit-btn"
                  >
                    {submittingFloorPlan ? 'Please wait...' : 'View Floor Plan'}
                  </button>
                </form>
              </div>
            </div>
          )}

          {project.highlights && (
            <div className="mt-12 bg-blue-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Highlights</h2>
              <p className="text-gray-700 leading-relaxed">{project.highlights}</p>
            </div>
          )}

          {/* Site Images Gallery */}
          {project.site_images && project.site_images.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {project.site_images_title || 'Actual Site'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {project.site_images.map((siteImage, index) => (
                  <div
                    key={index}
                    className="rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                    data-testid={`site-image-${index}`}
                    onClick={() => openLightbox(siteImage.url)}
                  >
                    <div className="bg-gray-100 flex items-center justify-center">
                      <img
                        src={siteImage.url}
                        alt={siteImage.caption || `Site image ${index + 1}`}
                        className="w-full h-auto object-contain"
                      />
                    </div>
                    {(siteImage.caption || siteImage.date) && (
                      <div className="p-4 bg-gray-50">
                        {siteImage.caption && (
                          <p className="text-gray-700 font-medium">{siteImage.caption}</p>
                        )}
                        {siteImage.date && (
                          <p className="text-sm text-gray-500 mt-1">{siteImage.date}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />

      {/* Floor Plan Viewer (shown after user submits the request form) */}
      {showFloorPlanViewer && selectedFloorPlan && (() => {
        const isPdf = (selectedFloorPlan || '').toLowerCase().split('?')[0].endsWith('.pdf');
        return (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowFloorPlanViewer(false)}
          data-testid="floor-plan-viewer"
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors bg-white/10 backdrop-blur-md rounded-full p-2 z-10"
            onClick={(e) => { e.stopPropagation(); setShowFloorPlanViewer(false); }}
            title="Close"
          >
            <X size={24} />
          </button>
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">
                {project.name} — Floor Plan
              </h3>
              <div className="flex items-center gap-2">
                <a
                  href={selectedFloorPlan}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-md text-gray-700"
                  data-testid="floorplan-open-newtab"
                >
                  Open in new tab
                </a>
                {!isPdf && (
                  <button
                    onClick={() => { setShowFloorPlanViewer(false); openLightbox(selectedFloorPlan); }}
                    className="text-sm bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-md text-white inline-flex items-center gap-1"
                    data-testid="floorplan-zoom-btn"
                  >
                    <Maximize2 size={14} /> Zoom & Pan
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 bg-gray-50 overflow-auto p-2 flex items-center justify-center">
              {isPdf ? (
                <iframe
                  src={`${API}/proxy-file?url=${encodeURIComponent(selectedFloorPlan)}#view=FitH&toolbar=1`}
                  title={`${project.name} floor plan`}
                  className="w-full h-[75vh] border-0 bg-white"
                  data-testid="floor-plan-pdf"
                />
              ) : (
                <img
                  src={selectedFloorPlan}
                  alt={`${project.name} floor plan`}
                  className="w-full h-auto max-h-[75vh] object-contain mx-auto cursor-zoom-in"
                  onClick={() => { setShowFloorPlanViewer(false); openLightbox(selectedFloorPlan); }}
                  data-testid="floor-plan-image"
                />
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* Lightbox Modal with Zoom Controls */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="lightbox-modal"
        >
          {/* Toolbar */}
          <div
            className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full px-3 py-2 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={zoomOut}
              disabled={zoom <= 1}
              className="text-white hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed rounded-full p-2 transition-colors"
              title="Zoom out"
              data-testid="zoom-out-btn"
            >
              <ZoomOut size={20} />
            </button>
            <span className="text-white text-sm font-medium min-w-[55px] text-center">{Math.round(zoom * 100)}%</span>
            <button
              onClick={zoomIn}
              disabled={zoom >= 5}
              className="text-white hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed rounded-full p-2 transition-colors"
              title="Zoom in"
              data-testid="zoom-in-btn"
            >
              <ZoomIn size={20} />
            </button>
            {zoom > 1 && (
              <button
                onClick={resetZoom}
                className="text-white hover:bg-white/20 rounded-full px-3 py-1 text-xs transition-colors ml-1"
                title="Reset"
              >
                Reset
              </button>
            )}
          </div>

          {/* Close */}
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-white/10 backdrop-blur-md rounded-full p-2"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            title="Close"
            data-testid="close-lightbox-btn"
          >
            <X size={24} />
          </button>

          {/* Image with pan-on-drag when zoomed */}
          <div
            className="w-full h-full flex items-center justify-center overflow-hidden p-4"
            onClick={(e) => e.stopPropagation()}
            onWheel={(e) => {
              if (e.deltaY < 0) zoomIn(); else zoomOut();
            }}
          >
            <img
              src={lightboxImage}
              alt="Full size view"
              draggable={false}
              onMouseDown={(e) => {
                if (zoom > 1) {
                  setIsDragging(true);
                  setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
                }
              }}
              onMouseMove={(e) => {
                if (isDragging && zoom > 1) {
                  setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
                }
              }}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                userSelect: 'none',
              }}
              onDoubleClick={() => (zoom === 1 ? zoomIn() : resetZoom())}
              className="max-w-full max-h-full object-contain"
              data-testid="lightbox-image"
            />
          </div>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs pointer-events-none">
            Scroll to zoom · Double-click to toggle · Drag to pan
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;